import { useState } from "react";
import { Download, Wallet, Receipt, Percent, HandCoins, BadgePercent } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePoll } from "@/hooks/usePoll";
import { rp, fmtDateTime, todayStr, METHOD_LABEL } from "@/lib/api";
import { PageHeader, Panel, StatCard } from "@/components/common";

function exportCsv(data, from, to) {
  const head = ["No Bill", "Pelanggan", "Meja", "Dibayar", "Metode", "Subtotal", "Diskon", "Service", "Pajak", "Total", "Kasir"];
  const lines = data.bills.map((b) => [b.code, b.customer_name, b.table_name, fmtDateTime(b.paid_at), METHOD_LABEL[b.payment.method], b.totals.subtotal, b.totals.discount || 0, b.totals.service, b.totals.tax, b.totals.total, b.cashier_name]
    .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
  const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `laporan-gomenu-${from}_${to}.csv`;
  a.click();
}

export default function Report() {
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const { data } = usePoll("/reports", 0, { date_from: from, date_to: to });
  const d = data || { count: 0, subtotal: 0, discount: 0, service: 0, tax: 0, total: 0, by_method: {}, top_items: [], bills: [] };

  return (
    <div>
      <PageHeader title="Laporan Transaksi" subtitle="Rekap penjualan berdasarkan periode">
        <Input data-testid="report-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 bg-white" />
        <span className="text-sm text-zinc-500">s/d</span>
        <Input data-testid="report-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 bg-white" />
        <Button data-testid="report-export-btn" variant="outline" disabled={!d.bills.length} onClick={() => exportCsv(d, from, to)}><Download className="mr-1 h-4 w-4" />Export CSV</Button>
      </PageHeader>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <StatCard testId="report-total" label="Total Pendapatan" value={rp(d.total)} icon={Wallet} />
        <StatCard testId="report-count" label="Jumlah Transaksi" value={d.count} icon={Receipt} accent="emerald" />
        <StatCard testId="report-discount" label="Total Diskon" value={rp(d.discount)} icon={BadgePercent} accent="stone" />
        <StatCard testId="report-service" label="Service Charge" value={rp(d.service)} icon={HandCoins} accent="amber" />
        <StatCard testId="report-tax" label="Pajak" value={rp(d.tax)} icon={Percent} accent="stone" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel className="p-5">
          <h3 className="mb-3 text-lg font-semibold">Per Metode Pembayaran</h3>
          <div className="space-y-2 text-sm" data-testid="report-by-method">
            {Object.entries(d.by_method).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-stone-100 pb-2"><span>{METHOD_LABEL[k]} <span className="text-zinc-400">({v.count})</span></span><span className="font-mono font-semibold">{rp(v.amount)}</span></div>
            ))}
          </div>
        </Panel>
        <Panel className="p-5">
          <h3 className="mb-3 text-lg font-semibold">Menu Terjual</h3>
          {d.top_items.length === 0 && <p className="text-sm text-zinc-500">Belum ada penjualan</p>}
          <div className="space-y-2 text-sm" data-testid="report-top-items">
            {d.top_items.map((t) => (
              <div key={t.name} className="flex justify-between"><span>{t.name} <span className="text-zinc-400">x{t.qty}</span></span><span className="font-mono">{rp(t.amount)}</span></div>
            ))}
          </div>
        </Panel>
      </div>
      <Panel className="mt-6 overflow-x-auto">
        <Table data-testid="report-table">
          <TableHeader><TableRow><TableHead>No. Bill</TableHead><TableHead>Pelanggan</TableHead><TableHead>Dibayar</TableHead><TableHead>Metode</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.bills.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-zinc-500">Tidak ada transaksi pada periode ini</TableCell></TableRow>}
            {d.bills.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-mono font-semibold">{b.code}</TableCell>
                <TableCell>{b.customer_name} &middot; {b.table_name}</TableCell>
                <TableCell className="text-sm text-zinc-500">{fmtDateTime(b.paid_at)}</TableCell>
                <TableCell>{METHOD_LABEL[b.payment.method]}</TableCell>
                <TableCell className="text-right font-mono">{rp(b.totals.subtotal)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{rp(b.totals.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}
